// Mirrors the API's autopilot contract: UpdateAutopilotSettingsDto,
// AutopilotSettings (schema) and AutopilotSettingsResponse in marabot-api.
// Keep the two in step — the settings page reads every field below.

export type AutopilotMode = "warmup" | "cruise" | "fullthrottle";

export type AutopilotCtaPlacement = "top" | "bottom";

export type AutopilotListingPriority = "price" | "newest";

export interface AutopilotSettings {
    enabled: boolean;
    mode: AutopilotMode;
    channels: string[];
    // False until the user has saved these settings once. While false the API
    // fills `channels` with every connected channel rather than the (empty)
    // stored list, so the page shows what Autopilot would actually post to.
    configuredByUser: boolean;
    openHouseSync: boolean;
    frontOfProperty: boolean;
    floorPlanPosts: boolean;
    ctaPlacement: AutopilotCtaPlacement;
    listingPriority: AutopilotListingPriority;
    // MB-1472: whether Autopilot includes the asking price in posts it writes
    // for commercial listings. Server-defaulted true; commercial-only in
    // effect but the field itself is a plain account-wide setting.
    commercialUsePrice?: boolean;
}

// Staged locally and only persisted on Save — the master switch included, so a
// mis-click on it can be undone before it deletes anything. configuredByUser is
// server-owned and never sent back.
export type StagedAutopilotSettings = Omit<
    AutopilotSettings,
    "configuredByUser"
>;

export interface AutopilotPrerequisites {
    planAllowsAutopilot: boolean;
    mlsApproved: boolean;
    autoIngestEnabled: boolean;
    agentMlsIdSaved: boolean;
    socialConnected: boolean;
    accountActive: boolean;
}

export interface AutopilotResponse {
    settings: AutopilotSettings;
    prerequisites: AutopilotPrerequisites;
    autopilotListingLimit: number;
    connectedChannels: string[];
    eligible: boolean;
    // MB-1532: Auto Pilot is on and still setting up (import in flight or a
    // campaign generating). OFF, Clear MLS and Remove MLS are refused with a
    // 423 while true, so the controls are disabled up front.
    busy: boolean;
}

export const AUTOPILOT_OFF_BUSY_TOOLTIP =
    "Auto Pilot can't be turned off while it's still importing your listings and creating campaigns. This page will unlock on its own in a few minutes.";

export const CLEAR_MLS_BUSY_TOOLTIP =
    "MLS details can't be cleared while Auto Pilot is still setting up your listings. This page will unlock on its own in a few minutes.";

// How often a page marked busy re-checks GET /settings/autopilot so the
// blocked control re-enables itself once setup finishes, without a reload.
export const AUTOPILOT_BUSY_POLL_MS = 30_000;

// Mirrors AutopilotReconcileResult in marabot-api. Both flags mean "nothing
// ran", so the counts below them are 0 and reporting them as an outcome would
// tell the user their listings were skipped.
export interface AutopilotReconcileResult {
    ran: boolean;
    created: number;
    converted: number;
    skipped: number;
    failed: number;
    // A concurrent reconcile held the per-user lock.
    lockBusy?: boolean;
    // MB-1420: an import is still processing, so adoption waits for it to
    // settle. The common case when Autopilot is enabled from the MLS wizard.
    deferred?: boolean;
    // Adoption by replacement: unpublished posts discarded across replaced
    // campaigns, and whether a manual promotion was taken over.
    discardedPosts?: number;
    promotionAdopted?: boolean;
    // Replaced campaigns that were actually recreated. `converted` counts
    // the removals and `created` includes these recreations. Absent from an
    // older api.
    replaced?: number;
}
